import React, { useState } from "react";
import styled from "styled-components";
import { useNavigate } from "react-router-dom";
import { registerCustomer } from "../../services/customerPortalApi";
import logger from "../../utils/logger";

const EMPTY_FORM = {
  firstname: "",
  lastname: "",
  email: "",
  phone_number: "",
  address: "",
  postal_code: "",
  city: "",
  country: "France",
};

export default function CustomerRegisterPage() {
  const navigate = useNavigate();
  const [form, setForm] = useState(EMPTY_FORM);
  const [message, setMessage] = useState("");
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const handleChange = (event) => {
    const { name, value } = event.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const validateForm = () => {
    const requiredFields = [
      "firstname",
      "lastname",
      "email",
      "phone_number",
      "address",
      "postal_code",
      "city",
      "country",
    ];

    for (const field of requiredFields) {
      if (!form[field]?.toString().trim()) {
        setError("Veuillez remplir tous les champs obligatoires.");
        return false;
      }
    }

    return true;
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setMessage("");
    setError("");

    if (!validateForm()) return;

    setSubmitting(true);

    try {
      const { data } = await registerCustomer(form);

      setMessage(
        data?.message ||
          "Votre compte client a été créé. Un e-mail vient de vous être envoyé pour définir votre mot de passe."
      );
      setForm(EMPTY_FORM);
    } catch (err) {
      logger.error(err);
      setError(
        err?.response?.data?.error ||
          err?.response?.data?.msg ||
          "Erreur lors de la création du compte client."
      );
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <PageWrapper>
      <RegisterPanel>
        <PanelBadge>Compte client</PanelBadge>
        <h1>Créer mon compte client</h1>
        <p>
          Renseignez vos informations. Un e-mail vous sera envoyé pour définir
          votre mot de passe et activer votre espace client.
        </p>

        {message && <PanelMessage>{message}</PanelMessage>}
        {error && <PanelMessage $error>{error}</PanelMessage>}

        {!message ? (
          <Form onSubmit={handleSubmit}>
            <FormSection>
              <SectionHeader>
                <SectionBadge>Identité</SectionBadge>
                <h4>Informations personnelles</h4>
              </SectionHeader>

              <FieldsGrid>
                <Field>
                  <label htmlFor="firstname">Prénom *</label>
                  <input
                    id="firstname"
                    name="firstname"
                    value={form.firstname}
                    onChange={handleChange}
                    required
                  />
                </Field>

                <Field>
                  <label htmlFor="lastname">Nom *</label>
                  <input
                    id="lastname"
                    name="lastname"
                    value={form.lastname}
                    onChange={handleChange}
                    required
                  />
                </Field>

                <Field $full>
                  <label htmlFor="email">E-mail *</label>
                  <input
                    id="email"
                    type="email"
                    name="email"
                    value={form.email}
                    onChange={handleChange}
                    autoComplete="email"
                    required
                  />
                </Field>

                <Field $full>
                  <label htmlFor="phone_number">Téléphone *</label>
                  <input
                    id="phone_number"
                    type="tel"
                    name="phone_number"
                    value={form.phone_number}
                    onChange={handleChange}
                    placeholder="+33..."
                    autoComplete="tel"
                    required
                  />
                </Field>
              </FieldsGrid>
            </FormSection>

            <FormSection>
              <SectionHeader>
                <SectionBadge>Coordonnées</SectionBadge>
                <h4>Adresse</h4>
              </SectionHeader>

              <FieldsGrid>
                <Field $full>
                  <label htmlFor="address">Adresse *</label>
                  <input
                    id="address"
                    name="address"
                    value={form.address}
                    onChange={handleChange}
                    autoComplete="street-address"
                    required
                  />
                </Field>

                <Field>
                  <label htmlFor="postal_code">Code postal *</label>
                  <input
                    id="postal_code"
                    name="postal_code"
                    value={form.postal_code}
                    onChange={handleChange}
                    autoComplete="postal-code"
                    required
                  />
                </Field>

                <Field>
                  <label htmlFor="city">Ville *</label>
                  <input
                    id="city"
                    name="city"
                    value={form.city}
                    onChange={handleChange}
                    autoComplete="address-level2"
                    required
                  />
                </Field>

                <Field $full>
                  <label htmlFor="country">Pays *</label>
                  <input
                    id="country"
                    name="country"
                    value={form.country}
                    onChange={handleChange}
                    autoComplete="country-name"
                    required
                  />
                </Field>
              </FieldsGrid>
            </FormSection>

            <SubmitButton type="submit" disabled={submitting}>
              {submitting ? "Création en cours..." : "Créer mon compte"}
            </SubmitButton>
          </Form>
        ) : (
          <Actions>
            <SubmitButton type="button" onClick={() => navigate("/")}>
              Retour à l’accueil
            </SubmitButton>
            <SecondaryButton type="button" onClick={() => navigate("/")}>
              J’ai déjà défini mon mot de passe
            </SecondaryButton>
          </Actions>
        )}
      </RegisterPanel>
    </PageWrapper>
  );
}

const PageWrapper = styled.main`
  min-height: 100dvh;
  width: 100vw;
  display: grid;
  place-items: center;
  padding: 1rem;
  box-sizing: border-box;
  background: #0b1328;
  overflow-y: auto;
`;

const RegisterPanel = styled.section`
  width: min(100%, 720px);
  max-height: calc(100dvh - 2rem);
  overflow-y: auto;
  padding: 1.6rem;
  border-radius: 24px;
  background: #1d263d;
  color: #f7f7f7;
  box-sizing: border-box;

  h1 {
    margin: 0.8rem 0 0.5rem;
    font-size: clamp(1.35rem, 4vw, 1.8rem);
    font-weight: 950;
  }

  p {
    margin: 0 0 1.2rem;
    opacity: 0.74;
    line-height: 1.45;
  }
`;

const PanelBadge = styled.div`
  display: inline-block;
  padding: 0.35rem 0.7rem;
  border-radius: 999px;
  background: #fff9ea;
  color: #0b1328;
  font-size: 0.75rem;
  font-weight: 950;
`;

const Form = styled.form`
  display: grid;
  gap: 0.95rem;
`;

const FormSection = styled.section`
  padding: 1rem;
  border-radius: 16px;
  background: #202a43;
  border: 1px solid rgba(255, 255, 255, 0.08);
`;

const SectionHeader = styled.div`
  margin-bottom: 0.9rem;

  h4 {
    margin: 0.45rem 0 0;
    font-size: 1rem;
    font-weight: 950;
  }
`;

const SectionBadge = styled.span`
  display: inline-block;
  width: fit-content;
  padding: 0.3rem 0.65rem;
  border-radius: 999px;
  background: #fff9ea;
  color: #0b1328;
  font-size: 0.72rem;
  font-weight: 950;
`;

const FieldsGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 0.75rem;

  @media (max-width: 640px) {
    grid-template-columns: 1fr;
  }
`;

const Field = styled.div`
  display: grid;
  gap: 0.35rem;
  grid-column: ${({ $full }) => ($full ? "1 / -1" : "auto")};

  label {
    font-size: 0.78rem;
    font-weight: 850;
    opacity: 0.82;
  }

  input {
    width: 100%;
    min-height: 42px;
    border: none;
    border-radius: 10px;
    padding: 0.75rem;
    background: #fff9ea;
    color: #0b1328;
    font-weight: 750;
    box-sizing: border-box;
    outline: none;
  }

  input:focus {
    box-shadow: 0 0 0 3px rgba(255, 249, 234, 0.22);
  }
`;

const SubmitButton = styled.button`
  width: 100%;
  border: none;
  border-radius: 12px;
  padding: 0.95rem 1rem;
  background: #fff9ea;
  color: #0b1328;
  font-weight: 950;
  cursor: ${({ disabled }) => (disabled ? "not-allowed" : "pointer")};
  opacity: ${({ disabled }) => (disabled ? 0.65 : 1)};
`;

const SecondaryButton = styled(SubmitButton)`
  background: transparent;
  color: #fff9ea;
  border: 1px solid rgba(255, 249, 234, 0.45);
`;

const Actions = styled.div`
  display: grid;
  gap: 0.75rem;
`;

const PanelMessage = styled.div`
  margin-bottom: 1rem;
  padding: 0.85rem 1rem;
  border-radius: 12px;
  font-weight: 850;
  background: ${({ $error }) =>
    $error ? "rgba(180, 40, 40, 0.9)" : "rgba(40, 140, 80, 0.9)"};
`;
